import { useMediaQuery, useTheme } from "@mui/material"
import React, { lazy, Suspense } from "react"
import { createBrowserRouter, Navigate } from "react-router-dom"
import { FullScreenLoader } from "../../components"
import { SubscriptionErrorBoundary } from "../../components/ErrorBoundary/SubscriptionErrorBoundary"
import { DisableMobile } from "../../components/ui/disableMobile"
import { useAppSelector } from "../../redux"
import {
  AgentScreen,
  AuthScreen as AuthScreenEnum,
  AvatarScreen,
  ConversationScreen,
  PlanFeatures,
  Screen,
  VideoScreen,
} from "../../types"
import ErrorPage from "../error-page"

// Constants
const IMPORT_ERROR_MESSAGES = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
]

// Lazy loading helper
const lazyLoad = (importFunc: () => Promise<any>) =>
  lazy(() =>
    importFunc()
      .then((module) => ({
        default: module.default || module[Object.keys(module)[0]],
      }))
      .catch(errorHandler),
  )

// Lazy loaded components
const LoginScreen = lazyLoad(() => import("../routes/LoginScreen/LoginScreen"))
const AuthScreen = lazyLoad(() => import("./Auth/AuthScreen"))
const ManageAccount = lazyLoad(() => import("./Auth/ManageAccount"))
const ResetPasswordScreen = lazyLoad(() => import("./Auth/ResetPassword"))
const NewPasswordScreen = lazyLoad(() => import("./Auth/NewPassword"))
const VerifyEmailScreen = lazyLoad(() => import("./Auth/VerifyEmail"))

const AvatarsScreen = lazyLoad(() => import("../routes/Avatars/AvatarsScreen"))
const PrepareScreen = lazyLoad(() => import("../routes/Avatars/Create/Prepare"))
const CheckInputsScreen = lazyLoad(
  () => import("../routes/Avatars/Create/CheckInputs"),
)
const UploadVideoScreen = lazyLoad(
  () => import("../routes/Avatars/Create/UploadVideo"),
)
const RecordingScreen = lazyLoad(
  () => import("../routes/Avatars/Create/Record"),
)
const SelectAvatarScreenNew = lazyLoad(() => import("./Shared/selectAvatar"))
const MyAgentsScreen = lazyLoad(() => import("./Agents/myAgents"))
const MyAgentsAgentInfoScreen = lazyLoad(() => import("./Agents/agentsInfo"))
const MyAgentsTrainAgentScreen = lazyLoad(() => import("./Agents/trainAgent"))
const MyAgentsFinalResultScreen = lazyLoad(() => import("./Agents/finalResult"))
const MyAgentsAgentDetailsScreen = lazyLoad(
  () => import("./Agents/agentDetails"),
)

const CreateVideoMessageScreen = lazyLoad(
  () => import("../routes/TextToVideo/CreateVideoMessage"),
)
const FinalResultScreen = lazyLoad(() => import("./TextToVideo/FinalResult"))

const SettingsScreen = lazyLoad(() => import("./Settings/Settings"))
const ApiSettingsScreen = lazyLoad(() => import("./ApiKeys"))

const AllConversations = lazyLoad(
  () => import("./Conversations/AllConversations"),
)
const ConversationDetails = lazyLoad(
  () => import("./Shared/ConversationDetails"),
)
const KnowledgeBase = lazyLoad(() => import("./KnowledgeBase/KnowledgeBase"))
const AnalyticsScreen = lazyLoad(() => import("./Analytics/Analytics"))
const SubscribeWithPlan = lazyLoad(
  () => import("./Subscription/SubscribeWithPlan"),
)

const Homepage = lazyLoad(() => import("./Home/Homepage"))
const TextToVideoScreen = lazyLoad(() => import("./TextToVideo/VideosScreen"))

const errorHandler = (e: any) => {
  if (IMPORT_ERROR_MESSAGES.some((msg) => e.message.includes(msg))) {
    window.location.reload()
    return {} as any
  } else {
    throw e
  }
}

// Route generation helper
const RouteGuard = ({ children }: { children: React.ReactNode }) => {
  const user = useAppSelector((state) => state.app.user)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  if (isMobile && user) {
    return <DisableMobile />
  }

  return <>{children}</>
}

const createSuspenseRoute = (
  Component: React.LazyExoticComponent<any>,
  {
    loader,
    useErrorBoundary = false,
    requiresAuth = true,
    requiresAdmin = false,
    requiredFeature,
  }: {
    loader?: any
    useErrorBoundary?: boolean
    requiresAuth?: boolean
    requiresAdmin?: boolean
    requiredFeature?: keyof PlanFeatures
  } = {},
) => {
  const element = (
    <RouteGuard>
      <Suspense fallback={<FullScreenLoader />}>
        {useErrorBoundary ? (
          <SubscriptionErrorBoundary>
            <Component />
          </SubscriptionErrorBoundary>
        ) : (
          <Component />
        )}
      </Suspense>
    </RouteGuard>
  )

  let wrappedElement = element
  if (requiredFeature) {
    wrappedElement = (
      <FeatureProtectedRoute feature={requiredFeature}>
        {wrappedElement}
      </FeatureProtectedRoute>
    )
  }
  if (requiresAdmin) {
    wrappedElement = <AdminProtectedRoute>{wrappedElement}</AdminProtectedRoute>
  }
  if (requiresAuth) {
    wrappedElement = <AuthProtectedRoute>{wrappedElement}</AuthProtectedRoute>
  }

  return {
    element: wrappedElement,
    loader,
  }
}

const AuthProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useAppSelector((state) => state.app.user)

  if (!user) {
    return <Navigate to="/auth" replace />
  }

  return <>{children}</>
}

const AdminProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isUserAdmin = useAppSelector((state) => state.app.isUserAdmin)

  if (!isUserAdmin) {
    return <Navigate to={`/${Screen.avatars}`} replace />
  }

  return <>{children}</>
}

const FeatureProtectedRoute = ({
  children,
  feature,
}: {
  children: React.ReactNode
  feature: keyof PlanFeatures
}) => {
  const usageDetails = useAppSelector((state) => state.app.usageDetails)
  const usageDetailsLoading = useAppSelector(
    (state) => state.app.usageDetailsLoading,
  )

  if (!usageDetails && usageDetailsLoading) {
    return <FullScreenLoader />
  }

  if (!usageDetails?.plan_features?.[feature]) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

const RootRoute = () => {
  const user = useAppSelector((state) => state.app.user)
  const [forceUpdate, setForceUpdate] = React.useState(0)

  React.useEffect(() => {
    setForceUpdate((prev) => prev + 1)
  }, [user])

  if (user) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <Homepage key={forceUpdate} />
      </Suspense>
    )
  }

  return (
    <Suspense fallback={<FullScreenLoader />}>
      <AuthScreen key={forceUpdate} />
    </Suspense>
  )
}

const AuthRoute = () => {
  const user = useAppSelector((state) => state.app.user)

  if (user) {
    return <Navigate to="/" replace />
  }

  return (
    <Suspense fallback={<FullScreenLoader />}>
      <AuthScreen />
    </Suspense>
  )
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <RouteGuard>
        <RootRoute />
      </RouteGuard>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.auth}`,
    element: (
      <RouteGuard>
        <AuthRoute />
      </RouteGuard>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.auth}/${AuthScreenEnum.manageAccount}`,
    ...createSuspenseRoute(ManageAccount, { requiresAuth: false }),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.auth}/${AuthScreenEnum.verifyEmail}`,
    ...createSuspenseRoute(VerifyEmailScreen, { requiresAuth: false }),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.auth}/${AuthScreenEnum.resetPassword}`,
    ...createSuspenseRoute(ResetPasswordScreen, { requiresAuth: false }),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.auth}/${AuthScreenEnum.newPassword}`,
    ...createSuspenseRoute(NewPasswordScreen, { requiresAuth: false }),
    errorElement: <ErrorPage />,
  },
  {
    path: Screen.avatars,
    ...createSuspenseRoute(AvatarsScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.avatars}/${AvatarScreen.prepare}`,
    ...createSuspenseRoute(PrepareScreen, { requiresAdmin: true }),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.avatars}/${AvatarScreen.checkInputs}`,
    ...createSuspenseRoute(CheckInputsScreen, { requiresAdmin: true }),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.avatars}/${AvatarScreen.uploadVideo}`,
    ...createSuspenseRoute(UploadVideoScreen, { requiresAdmin: true }),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.avatars}/${AvatarScreen.recordVideo}`,
    ...createSuspenseRoute(RecordingScreen, { requiresAdmin: true }),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.videos}`,
    ...createSuspenseRoute(TextToVideoScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.videos}/${VideoScreen.selectVideoAvatar}`,
    ...createSuspenseRoute(SelectAvatarScreenNew),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.videos}/${VideoScreen.createVideoMessage}`,
    ...createSuspenseRoute(CreateVideoMessageScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.videos}/${VideoScreen.finalResult}`,
    ...createSuspenseRoute(FinalResultScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.myAgents}`,
    ...createSuspenseRoute(MyAgentsScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.myAgents}/${AgentScreen.selectAvatar}`,
    ...createSuspenseRoute(SelectAvatarScreenNew),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.myAgents}/${AgentScreen.agentsInfo}`,
    ...createSuspenseRoute(MyAgentsAgentInfoScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.myAgents}/${AgentScreen.trainAgent}`,
    ...createSuspenseRoute(MyAgentsTrainAgentScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.myAgents}/${AgentScreen.finalResult}`,
    ...createSuspenseRoute(MyAgentsFinalResultScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.myAgents}/${AgentScreen.agentDetails}`,
    ...createSuspenseRoute(MyAgentsAgentDetailsScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.myAgents}/${ConversationScreen.conversationDetails}`,
    ...createSuspenseRoute(ConversationDetails),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.settings}`,
    ...createSuspenseRoute(SettingsScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.apiKeys}`,
    ...createSuspenseRoute(ApiSettingsScreen),
    errorElement: <ErrorPage />,
  },

  {
    path: `${Screen.conversations}`,
    ...createSuspenseRoute(AllConversations),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.conversations}/${ConversationScreen.conversationDetails}`,
    ...createSuspenseRoute(ConversationDetails),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.knowledgeBase}`,
    ...createSuspenseRoute(KnowledgeBase),
    errorElement: <ErrorPage />,
  },
  {
    path: `${Screen.analytics}`,
    ...createSuspenseRoute(AnalyticsScreen),
    errorElement: <ErrorPage />,
  },
  {
    path: "subscribe",
    ...createSuspenseRoute(SubscribeWithPlan, { useErrorBoundary: true }),
    errorElement: <ErrorPage />,
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
])
